import ButtonController from '@mm/state/controllers/ButtonController';
import ButtonModel from '@mm/state/model/ButtonModel';
import PageGroup from '@mm/state/model/PageGroupModel';
import Page from '@mm/state/model/PageModel';
import React from 'react';
import { compose, withHandlers, withProps, withStateHandlers } from 'recompose';
import { getUpdatedTextWithEmoji } from '../../../../../common/components/EmojiPicker/emoji-picker-helpers';
import { EmojiPicker } from '../../../../../common/components/EmojiPicker/EmojiPicker';
import {
  generatePageStructure,
  nameById,
} from '../../../../../common/components/FilterableSelect/filterable-select-helpers';
import Select from '../../../../../common/components/FilterableSelect/FilterableSelect';
import TextInput from '../../../../../common/components/TextInput';
import { QUICK_REPLY_EMPTY_ERROR_MESSAGE } from '../constants';
import './OptinQuickReply.scss';

type PropsFromWithHandlers = {
  updateQuickReplyText: ReturnType<typeof updateQuickReplyText>;
  updateSelectedPage: ReturnType<typeof updateSelectedPage>;
  updateInsertEmoji: (emoji: string) => string;
};

type PropsFromWithProps = {
  selectedPageId: number;
};

type PropsFromWithState = {
  error: string;
  setError: (value: string) => string;
  quickReplyText: string;
  quickReplyInputRef: HTMLInputElement;
  setQuickReplyText: (value: string) => string;
  setQuickReplyInputRef: (value: HTMLInputElement) => string;
};

type InnerProps = PropsFromWithState &
  PropsFromWithHandlers &
  PropsFromWithProps;

type OnQuickReplyUpdate = () => void;

type OuterProps = {
  quickReply: ButtonModel;
  order: number;
  pages: Page[];
  pageGroups: PageGroup[];
  onUpdate: OnQuickReplyUpdate;
  onDelete: (value: number) => void;
  widgetId: number;
};

type Props = InnerProps & OuterProps;

const OptinQuickReply = ({
  order,
  pages,
  pageGroups,
  quickReply,
  quickReplyText,
  setQuickReplyText,
  updateQuickReplyText,
  updateSelectedPage,
  onDelete,
  selectedPageId,
  updateInsertEmoji,
  setQuickReplyInputRef,
  error,
}: Props) => (
  <div className="optin-quick-reply__row">
    <div className="optin-quick-reply__input-group">
      <div className="optin-quick-reply__title">Answer {order}</div>
      <div className="optin-quick-reply__input">
        <div>
          <TextInput
            value={quickReplyText}
            onChange={e => setQuickReplyText(e.target.value)}
            onBlur={updateQuickReplyText}
            onMouseLeave={updateQuickReplyText}
            maxLength={20}
            inputRef={setQuickReplyInputRef}
          />
          <EmojiPicker
            className="optin-quick-reply__emoji"
            onEmojiPicked={updateInsertEmoji}
            additionalDropdownStyles={{
              right: '',
              left: '-50px',
            }}
          />
        </div>
        <div className="optin-quick-reply__error">{error}</div>
      </div>
      <div className="optin-quick-reply__select">
        <Select
          outerValue={nameById(selectedPageId, pages)}
          onChange={updateSelectedPage}
          suggestions={generatePageStructure(pages, pageGroups)}
        />
      </div>
    </div>
    <div className="optin-quick-reply__action">
      {order > 1 ? (
        <span
          className="optin-quick-reply__btn"
          onClick={() => onDelete(quickReply.id)}
        >
          <i className="fa fa-trash" />
        </span>
      ) : null}
    </div>
  </div>
);

const updateQuickReplyText = (props: Props) => () => {
  const {
    quickReply,
    quickReplyText,
    onUpdate,
    widgetId,
    setError,
    selectedPageId,
  } = props;
  if (quickReplyText) {
    if (quickReplyText !== quickReply.name) {
      const buttonConfig = { pageId: selectedPageId };
      const button = Object.assign({}, quickReply, {
        config: buttonConfig,
        name: quickReplyText,
      });
      updateQuickReply(widgetId, button, onUpdate);
      setError('');
    }
  } else {
    setError(QUICK_REPLY_EMPTY_ERROR_MESSAGE);
  }
};

const updateInsertEmoji = (props: Props) => (emoji: string) => {
  const {
    quickReplyInputRef,
    quickReplyText,
    onUpdate,
    widgetId,
    quickReply,
    selectedPageId,
    setQuickReplyText,
    updateQuickReplyText,
    setError,
  } = props;
  const nextQuickReplyText = getUpdatedTextWithEmoji(
    quickReplyText,
    emoji,
    quickReplyInputRef,
  );
  const buttonConfig = { pageId: selectedPageId };
  const button = Object.assign({}, quickReply, {
    config: buttonConfig,
    name: nextQuickReplyText,
  });

  setError('');
  setQuickReplyText(nextQuickReplyText);
  updateQuickReply(widgetId, button, onUpdate);
};

const updateSelectedPage = (props: Props) => (nextSelectedPageId: number) => {
  const { quickReply, onUpdate, widgetId, selectedPageId } = props;
  if (nextSelectedPageId !== selectedPageId) {
    const buttonConfig = { pageId: nextSelectedPageId };
    const button = Object.assign({}, quickReply, { config: buttonConfig });
    updateQuickReply(widgetId, button, onUpdate);
  }
};

const updateQuickReply = async (
  widgetId: number,
  quickReply: ButtonModel,
  onUpdate: OnQuickReplyUpdate,
) => {
  await ButtonController.putButton(widgetId, quickReply);
  onUpdate();
};

const initialState = ({ quickReply }) => ({
  error: '',
  quickReplyInputRef: undefined,
  quickReplyText: quickReply.name,
});

const setError = () => (error: string) => ({ error });
const setQuickReplyInputRef = () => quickReplyInputRef => ({
  quickReplyInputRef,
});
const setQuickReplyText = () => quickReplyText => ({ quickReplyText });

const enhance = compose<InnerProps, OuterProps>(
  withProps(({ quickReply }) => ({ selectedPageId: quickReply.config.pageId })),
  withStateHandlers(initialState, {
    setError,
    setQuickReplyInputRef,
    setQuickReplyText,
  }),
  withHandlers({ updateInsertEmoji, updateQuickReplyText, updateSelectedPage }),
);

export default enhance(OptinQuickReply);
